import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import {
  Receipt,
  Utensils,
  ShoppingBag,
  Truck,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  RotateCw,
  Loader2,
  LayoutGrid,
  ChefHat,
  Clock,
  Users,
  Flame,
  CircleDot,
  ChevronRight,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import api from "@/lib/api";
import RamadanBanner from "@/components/common/RamadanBanner";
import ClockWeatherWidget from "@/components/dashboard/ClockWeatherWidget";
import { aed } from "@/lib/format";
import { getDashboardOrderDestination } from "@/lib/dashboardOrderNav";
import { cn } from "@/lib/utils";

const CHANNELS = [
  { key: "dine_in_sales", labelKey: "dashboard.dineIn", icon: Utensils, color: "#F36F21", soft: "#FFF3E8" },
  { key: "take_away_sales", labelKey: "dashboard.takeAway", icon: ShoppingBag, color: "#2563EB", soft: "#EFF6FF" },
  { key: "delivery_sales", labelKey: "dashboard.delivery", icon: Truck, color: "#7C3AED", soft: "#F5F3FF" },
];

const KOT_STATUS_STYLE = {
  New: { bg: "#FFF3E8", text: "#C2410C", ring: "#FDBA74" },
  Preparing: { bg: "#EFF6FF", text: "#1D4ED8", ring: "#93C5FD" },
  Ready: { bg: "#ECFDF5", text: "#047857", ring: "#6EE7B7" },
};

const ORDER_STATUS_STYLE = {
  Paid: "rc-pill rc-pill--green",
  Held: "rc-pill rc-pill--amber",
  Open: "rc-pill rc-pill--orange",
  Dispatched: "rc-pill rc-pill--purple",
  Delivered: "rc-pill rc-pill--purple",
};

function DeltaBadge({ delta, vsPriorLabel }) {
  if (delta === null || delta === undefined) {
    return <span className="text-brand-brown/35 text-xs">— {vsPriorLabel}</span>;
  }
  const up = delta >= 0;
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs font-semibold", up ? "text-emerald-600" : "text-red-600")}>
      {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
      {up ? "+" : ""}
      {delta}%
      <span className="font-normal text-brand-brown/40">{vsPriorLabel}</span>
    </span>
  );
}

function OpsCard({ className, children, padding = true, delay = 0 }) {
  return (
    <div
      className={cn("rc-card rc-fade-in", padding && "rc-card--padded", className)}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function restaurantLabel(branch) {
  const company = String(branch?.company_name || "").trim();
  const brand = String(branch?.brand_name || "").trim();
  return company || brand || String(branch?.name || "").trim();
}

/** Restaurant name always; branch name only when a branch is selected. */
function dashboardIdentity(user) {
  const branches = user?.branches ?? [];
  const selectedId = user?.default_branch?.id;
  const selected = selectedId
    ? branches.find((b) => Number(b.id) === Number(selectedId))
    : null;
  const fallback = selected || (branches.length === 1 ? branches[0] : branches[0]);
  const restaurant = restaurantLabel(fallback) || user?.default_branch?.name || "Restaurant";
  const branchName = selected ? String(selected.name || user?.default_branch?.name || "").trim() : "";
  const showBranch = Boolean(branchName && branchName !== restaurant);
  return { restaurant, branchName: showBranch ? branchName : "" };
}

function SectionHead({ title, subtitle, action, live }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <div className="flex items-center gap-2">
          {live && <span className="rc-live-dot" title="Live" />}
          <h2 className="rc-section-title">{title}</h2>
        </div>
        {subtitle && <p className="rc-section-sub">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function StatusTile({ icon: Icon, label, value, tone = "default", to }) {
  const tones = {
    default: "bg-white text-brand-brown",
    orange: "bg-[#FFF3E8] text-[#C2410C]",
    blue: "bg-[#EFF6FF] text-[#1D4ED8]",
    green: "bg-[#ECFDF5] text-[#047857]",
    purple: "bg-[#F5F3FF] text-[#6D28D9]",
  };

  const inner = (
    <>
      <span className={cn("rc-status-icon", tones[tone])}>
        <Icon size={16} />
      </span>
      <span className="rc-status-value">{value}</span>
      <span className="rc-status-label">{label}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className="rc-status-tile rc-status-tile--link">
        {inner}
        <ChevronRight size={14} className="rc-status-chevron" />
      </Link>
    );
  }

  return <div className="rc-status-tile">{inner}</div>;
}

function TableChip({ table }) {
  const busy = table.status === "Occupied" || table.status === "Printed" || table.status === "Multi";
  return (
    <Link to="/waiter" className={cn("rc-table-chip", busy && "rc-table-chip--busy")}>
      <span className="rc-table-chip-code">{table.code}</span>
      <span className="rc-table-chip-meta">
        {table.phase || table.status}
        {table.elapsed_minutes != null && ` · ${table.elapsed_minutes}m`}
      </span>
      {table.amount > 0 && <span className="rc-table-chip-amt">{aed(table.amount)}</span>}
    </Link>
  );
}

function KitchenRow({ ticket }) {
  const style = KOT_STATUS_STYLE[ticket.status] ?? KOT_STATUS_STYLE.New;
  return (
    <Link to="/kds" className="rc-kitchen-row">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="rc-kot-badge" style={{ background: style.bg, color: style.text }}>
          #{ticket.kot_number}
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-brand-brown truncate">
            {ticket.table} · {ticket.item_count} items
          </div>
          <div className="text-[11px] text-brand-brown/50 truncate">
            {(ticket.items ?? []).join(", ") || "—"}
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <span
          className="rc-pill text-[10px]"
          style={{ background: style.bg, color: style.text, borderColor: style.ring }}
        >
          {ticket.status}
        </span>
        <div className="text-[11px] text-brand-brown/45 mt-1 flex items-center justify-end gap-1">
          <Clock size={11} />
          {ticket.elapsed}m
        </div>
      </div>
    </Link>
  );
}

function OrderCard({ order, onClick, clickable }) {
  const typeIcon = order.type === "Delivery" ? Truck : order.type === "Take Away" ? ShoppingBag : Utensils;
  const TypeIcon = typeIcon;
  const pillClass = ORDER_STATUS_STYLE[order.status] ?? "rc-pill";

  return (
    <button
      type="button"
      className={cn("rc-order-card", clickable && "rc-order-card--clickable")}
      onClick={onClick}
      disabled={!clickable}
      data-testid={`order-row-${order.sales_id}`}
    >
      <div className="rc-order-card-left">
        <span className="rc-order-type-icon">
          <TypeIcon size={16} />
        </span>
        <div className="text-left min-w-0">
          <div className="text-[14px] font-bold text-brand-brown font-mono">{order.bill_number || order.id}</div>
          <div className="text-[12px] text-brand-brown/55 truncate">
            {order.type}
            {order.table && order.table !== "—" ? ` · ${order.table}` : ""}
            {order.customer && order.customer !== "Walk-in" ? ` · ${order.customer}` : ""}
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[15px] font-extrabold text-brand-brown">{aed(order.amount)}</div>
        <div className="flex items-center justify-end gap-2 mt-1">
          <span className={pillClass}>{order.status}</span>
          <span className="text-[11px] text-brand-brown/40">{order.time}</span>
        </div>
      </div>
    </button>
  );
}

function StaffRow({ staff, rank }) {
  return (
    <div className="rc-staff-row" data-testid={`staff-row-${rank}`}>
      <span className={cn("rc-staff-rank", rank === 1 && "rc-staff-rank--top")}>{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-bold text-brand-brown truncate">{staff.name}</div>
        <div className="text-[11px] text-brand-brown/50">
          {staff.bill_count} bills · {aed(staff.net_sales)}
        </div>
        <div className="rc-progress mt-2">
          <div className="rc-progress-bar" style={{ width: `${staff.share_pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function TopItemRow({ item, rank, maxSold, soldLabel }) {
  return (
    <div className="rc-staff-row" data-testid={`top-item-${rank - 1}`}>
      <span className={cn("rc-staff-rank", rank === 1 && "rc-staff-rank--top")}>{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-bold text-brand-brown truncate">{item.name}</div>
        <div className="text-[11px] text-brand-brown/50">
          {item.sold} {soldLabel} · {aed(item.revenue)}
        </div>
        <div className="rc-progress mt-2">
          <div
            className="rc-progress-bar"
            style={{ width: `${Math.min(100, (item.sold / Math.max(maxSold, 1)) * 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const branchId = user?.default_branch?.id;
  const multiBranch = isMultiBranchFeatureEnabled(user);
  const [period, setPeriod] = useState("today");

  const identity = useMemo(() => dashboardIdentity(user), [user]);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["dashboard", branchId, period],
    enabled: !!branchId,
    refetchInterval: period === "today" ? 30_000 : false,
    queryFn: async () => {
      const { data: res } = await api.get("/dashboard", {
        params: { branch_id: branchId, period },
      });
      return res;
    },
  });

  const { data: enterprise } = useQuery({
    queryKey: ["dashboard-enterprise", period],
    enabled: multiBranch,
    queryFn: async () => {
      const { data: res } = await api.get("/dashboard/enterprise", { params: { period } });
      return res;
    },
  });

  const { data: restaurantSettings } = useQuery({
    queryKey: ["restaurant-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/restaurant-settings", { params: { branch_id: branchId } });
      return res.settings;
    },
    enabled: Boolean(branchId),
    staleTime: 60_000,
  });

  const kpis = data?.kpis;
  const live = data?.live_status;
  const openTables = data?.open_tables;
  const kitchen = data?.kitchen_queue;
  const staff = data?.staff_performance ?? [];
  const hourlySales = data?.hourly_sales ?? [];
  const recentOrders = data?.recent_orders ?? [];
  const topItems = data?.top_items ?? [];
  const maxSold = Math.max(...topItems.map((x) => x.sold), 1);

  const periodLabels = {
    today: t("dashboard.today"),
    yesterday: t("dashboard.yesterday"),
    week: t("dashboard.thisWeek"),
    month: t("dashboard.thisMonth"),
  };

  const netSales = Number(kpis?.net_sales ?? 0);
  const channels = useMemo(
    () =>
      CHANNELS.map((ch) => {
        const amount = Number(kpis?.[ch.key] ?? 0);
        return {
          ...ch,
          label: t(ch.labelKey),
          amount,
          pct: netSales > 0 ? Math.round((amount / netSales) * 100) : 0,
        };
      }),
    [kpis, netSales, t],
  );

  function handleOrderClick(order) {
    const dest = getDashboardOrderDestination(order, {
      fromDate: data?.from_date,
      toDate: data?.to_date,
    });
    if (dest) navigate(dest.pathname, { state: dest.state });
  }

  const refreshedAt = live?.refreshed_at
    ? new Date(live.refreshed_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <PageContainer className="rc-page">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-2.5 mb-2.5">
        <header className="rc-header xl:col-span-8 mb-0">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="rc-live-dot shrink-0 self-center" />
              <h1 className="rc-shop-title">
                {identity.restaurant}
                {identity.branchName ? (
                  <>
                    <span className="rc-shop-sep" aria-hidden>
                      ·
                    </span>
                    <span className="rc-shop-branch">{identity.branchName}</span>
                  </>
                ) : null}
              </h1>
            </div>
            <p className="rc-page-kicker">
              {t("dashboard.title")}
              <span className="rc-header-badge">{t("dashboard.commandCenter")}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {refreshedAt && period === "today" && (
              <span className="text-[11px] text-brand-brown/45 hidden md:inline me-1">
                {t("dashboard.updatedAt", { time: refreshedAt })}
              </span>
            )}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[118px] h-8 text-[12px] rc-select" data-testid="dashboard-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">{t("dashboard.today")}</SelectItem>
                <SelectItem value="yesterday">{t("dashboard.yesterday")}</SelectItem>
                <SelectItem value="week">{t("dashboard.thisWeek")}</SelectItem>
                <SelectItem value="month">{t("dashboard.thisMonth")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rc-select"
              data-testid="dashboard-refresh"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            </Button>
          </div>
        </header>
        <div className="xl:col-span-4 min-h-0">
          <ClockWeatherWidget variant="bar" className="h-full" />
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 shadow-sm">
          {t("dashboard.loadError")} {error.response?.data?.message || error.message}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-brand-brown/50 gap-3">
          <Loader2 className="animate-spin text-brand-orange" size={22} />
          <span className="text-[14px]">{t("dashboard.loading")}</span>
        </div>
      ) : (
        <div className="rc-stack">
          <RamadanBanner settings={restaurantSettings} />

          {multiBranch && enterprise && (
            <OpsCard className="rc-enterprise-strip" delay={40}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
                <div>
                  <h2 className="rc-section-title">{t("dashboard.allBranches", { count: enterprise.branch_count })}</h2>
                  <p className="rc-section-sub">{t("dashboard.networkOverview")}</p>
                </div>
                <Link to="/reports/branch-comparison" className="rc-text-link">
                  {t("dashboard.branchComparison")} <ChevronRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-2.5">
                <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: t("dashboard.sales"), value: aed(enterprise.totals?.net_sales) },
                    { label: t("dashboard.orders"), value: enterprise.totals?.bill_count },
                    { label: t("dashboard.avgBill"), value: aed(enterprise.totals?.avg_bill) },
                    { label: t("dashboard.delivery"), value: aed(enterprise.totals?.delivery_sales) },
                  ].map((item) => (
                    <div key={item.label} className="rc-mini-stat">
                      <span className="rc-mini-stat-label">{item.label}</span>
                      <span className="rc-mini-stat-value">{item.value}</span>
                    </div>
                  ))}
                </div>
                <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                  {(enterprise.branches ?? []).slice(0, 6).map((b) => (
                    <div key={b.branch_id} className="rc-branch-chip rc-branch-chip--row">
                      <div className="min-w-0">
                        <div className="font-bold text-brand-brown truncate">{b.branch_name}</div>
                        <div className="text-brand-brown/50 text-[11px]">{b.city || "—"}</div>
                      </div>
                      <span className="font-mono text-brand-orange font-bold shrink-0">{aed(b.net_sales)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </OpsCard>
          )}

          {/* Hero sales */}
          <OpsCard className="rc-hero-sales" padding={false} delay={80}>
            <div className="rc-hero-sales-inner">
              <div className="rc-hero-sales-main">
                <p className="rc-hero-eyebrow">
                  {periodLabels[period]} {t("dashboard.netSales")}
                </p>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
                  <span className="rc-hero-amount" data-testid="kpi-sales">{aed(kpis?.net_sales ?? 0)}</span>
                  <DeltaBadge delta={kpis?.net_sales_delta} vsPriorLabel={t("dashboard.vsPrior")} />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3 max-w-lg">
                  <div data-testid="kpi-orders">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-brown/45">{t("dashboard.orders")}</div>
                    <div className="text-[20px] font-extrabold text-brand-brown">{kpis?.bill_count ?? 0}</div>
                  </div>
                  <div data-testid="kpi-avg">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-brown/45">{t("dashboard.avgBill")}</div>
                    <div className="text-[20px] font-extrabold text-brand-brown">{aed(kpis?.avg_bill ?? 0)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-brown/45">{t("dashboard.covers")}</div>
                    <div className="text-[20px] font-extrabold text-brand-brown">{live?.guests_seated ?? 0}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {channels.map((ch) => {
                    const Icon = ch.icon;
                    return (
                      <div key={ch.key} className="rc-channel-pill" data-testid={`kpi-${ch.key}`}>
                        <span className="rc-channel-icon" style={{ background: ch.soft, color: ch.color }}>
                          <Icon size={14} />
                        </span>
                        <span className="text-[12px] font-semibold text-brand-brown">{ch.label}</span>
                        <span className="text-[13px] font-bold text-brand-brown">{aed(ch.amount)}</span>
                        <span className="text-[11px] text-brand-brown/40">{ch.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {hourlySales.length > 0 && (
                <div className="rc-hero-chart">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-brown/40 mb-2">
                    {t("dashboard.salesPace")}
                  </p>
                  <div className="h-[120px] w-full min-w-0">
                    <ResponsiveContainer width="100%" height={120} minWidth={0}>
                      <AreaChart data={hourlySales} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="rcSalesGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#F36F21" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#F36F21" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="hour" tickLine={false} axisLine={false} tick={{ fontSize: 9, fill: "#9A7B6E" }} interval={3} />
                        <YAxis hide />
                        <Tooltip
                          formatter={(v) => [aed(v), t("dashboard.sales")]}
                          contentStyle={{ borderRadius: 12, border: "1px solid #F0E0D4", fontSize: 12 }}
                        />
                        <Area type="monotone" dataKey="sales" stroke="#F36F21" fill="url(#rcSalesGrad)" strokeWidth={2.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </OpsCard>

          {/* Live + kitchen load */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-2.5">
            <OpsCard className="xl:col-span-7" delay={120}>
              <SectionHead title={t("dashboard.liveStatus")} subtitle={t("dashboard.liveStatusHint")} live />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatusTile icon={CircleDot} label={t("dashboard.openOrders")} value={live?.open_orders ?? 0} tone="orange" to="/pos" />
                <StatusTile icon={Receipt} label={t("dashboard.heldBills")} value={live?.held_bills ?? 0} tone="blue" to="/pos/held-bills" />
                <StatusTile icon={Truck} label={t("dashboard.deliveryOut")} value={live?.delivery_active ?? 0} tone="purple" to="/delivery" />
                <StatusTile icon={Users} label={t("dashboard.guestsSeated")} value={live?.guests_seated ?? 0} tone="green" to="/waiter" />
              </div>
            </OpsCard>

            <OpsCard className="xl:col-span-5" delay={160}>
              <SectionHead
                title={t("dashboard.kitchenLoad")}
                action={
                  <Link to="/kds" className="rc-text-link">
                    {t("dashboard.openKds")} <ChevronRight size={14} />
                  </Link>
                }
                live
              />
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "new", label: t("dashboard.kotNew"), count: live?.kitchen?.new ?? 0 },
                  { key: "preparing", label: t("dashboard.kotPreparing"), count: live?.kitchen?.preparing ?? 0 },
                  { key: "ready", label: t("dashboard.kotReady"), count: live?.kitchen?.ready ?? 0 },
                ].map((k) => (
                  <div key={k.key} className="rc-kitchen-stat">
                    <Flame size={14} className="text-brand-orange mb-1" />
                    <div className="text-[20px] font-extrabold text-brand-brown">{k.count}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-brand-brown/45">{k.label}</div>
                  </div>
                ))}
              </div>
            </OpsCard>
          </div>

          {/* Open tables + kitchen queue */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-2.5">
            <OpsCard className="xl:col-span-3" delay={240}>
              <SectionHead
                title={t("dashboard.openTables")}
                subtitle={t("dashboard.openTablesHint", {
                  occupied: live?.guests_seated ?? 0,
                  total: live?.tables_total ?? 0,
                  pct: openTables?.occupancy_pct ?? 0,
                })}
                action={
                  <Link to="/waiter" className="rc-text-link">
                    {t("dashboard.floorPlan")} <ChevronRight size={14} />
                  </Link>
                }
                live
              />
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="rc-floor-stat">{openTables?.counts?.Vacant ?? 0} {t("dashboard.vacant")}</span>
                <span className="rc-floor-stat rc-floor-stat--busy">{live?.guests_seated ?? 0} {t("dashboard.seated")}</span>
                <span className="rc-floor-stat">{openTables?.counts?.Reserved ?? 0} {t("dashboard.reserved")}</span>
              </div>
              {(openTables?.occupied ?? []).length === 0 ? (
                <div className="rc-empty-state">
                  <LayoutGrid size={28} className="text-brand-brown/20 mb-2" />
                  <p>{t("dashboard.noOpenTables")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {(openTables?.occupied ?? []).map((table) => (
                    <TableChip key={table.id} table={table} />
                  ))}
                </div>
              )}
            </OpsCard>

            <OpsCard className="xl:col-span-2" delay={280}>
              <SectionHead
                title={t("dashboard.kitchenQueue")}
                subtitle={t("dashboard.kitchenQueueHint", { count: kitchen?.counts?.total ?? 0 })}
                action={
                  <Link to="/kds" className="rc-text-link">
                    {t("dashboard.fullKds")} <ChevronRight size={14} />
                  </Link>
                }
                live
              />
              {(kitchen?.tickets ?? []).length === 0 ? (
                <div className="rc-empty-state">
                  <ChefHat size={28} className="text-brand-brown/20 mb-2" />
                  <p>{t("dashboard.noKitchenTickets")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {kitchen.tickets.map((ticket) => (
                    <KitchenRow key={ticket.id} ticket={ticket} />
                  ))}
                </div>
              )}
            </OpsCard>
          </div>

          {/* Top items + recent orders + staff */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-2.5">
            <OpsCard className="xl:col-span-3" delay={320}>
              <SectionHead title={t("dashboard.topSellingItems")} subtitle={periodLabels[period]} />
              {topItems.length === 0 ? (
                <div className="rc-empty-state">
                  <TrendingUp size={28} className="text-brand-brown/20 mb-2" />
                  <p>{t("dashboard.noItemSales")}</p>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {topItems.map((item, i) => (
                    <TopItemRow key={item.name} item={item} rank={i + 1} maxSold={maxSold} soldLabel={t("dashboard.sold")} />
                  ))}
                </div>
              )}
            </OpsCard>

            <OpsCard className="xl:col-span-6" delay={360}>
              <SectionHead
                title={t("dashboard.recentOrders")}
                subtitle={t("dashboard.recentOrdersHint")}
              />
              {recentOrders.length === 0 ? (
                <div className="rc-empty-state">
                  <Receipt size={28} className="text-brand-brown/20 mb-2" />
                  <p>{t("dashboard.noOrdersPeriod")}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {recentOrders.slice(0, 10).map((order, idx) => {
                    const clickable = Boolean(
                      getDashboardOrderDestination(order, {
                        fromDate: data?.from_date,
                        toDate: data?.to_date,
                      }),
                    );
                    return (
                      <OrderCard
                        key={`${order.sales_id ?? order.id}-${idx}`}
                        order={order}
                        clickable={clickable}
                        onClick={() => clickable && handleOrderClick(order)}
                      />
                    );
                  })}
                </div>
              )}
            </OpsCard>

            <OpsCard className="xl:col-span-3" delay={400}>
              <SectionHead title={t("dashboard.staffPerformance")} subtitle={periodLabels[period]} />
              {staff.length === 0 ? (
                <div className="rc-empty-state">
                  <Users size={28} className="text-brand-brown/20 mb-2" />
                  <p>{t("dashboard.noStaffData")}</p>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {staff.map((member, i) => (
                    <StaffRow key={member.user_id ?? i} staff={member} rank={i + 1} />
                  ))}
                </div>
              )}
            </OpsCard>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
